import { TreeObjectId } from '@cbsa/state/modules/universal';
import { createSelector } from 'reselect';
import { TrashState } from './models';
import { namespace } from './store';

export const selectStore = (state: { [namespace]: TrashState }) =>
  state[namespace];

export const selectTrashTdos = createSelector(selectStore, (s) => s.files.tdos);

// The selected TDOs, in selection order, resolved even when ticked on another
// page and so no longer in `tdos` (current page only). `undefined` marks an id
// neither can resolve. There is deliberately no page-only lookup: acting on the
// selection through one silently drops rows.
// Lifetime: the snapshot behind this is cleared when leaving Trash, so a saga
// acting on the selection must read it before its first await.
export const selectSelectedTdoRows = createSelector(selectStore, (s) =>
  s.files.selectedItems.map(
    (id) => s.files.tdos[id] ?? s.files.selectedTdos[id]
  )
);

export const selectFileSelectedItems = createSelector(
  selectStore,
  (s) => s.files.selectedItems
);

export const selectIsFetchingFileData = createSelector(
  selectStore,
  (s) => s.files.isFetchingData
);

export const selectFilePageSize = createSelector(
  selectStore,
  (s) => s.files.pageSize
);
export const selectFilePageIndex = createSelector(
  selectStore,
  (s) => s.files.pageIndex
);
export const selectFileHasNextPage = createSelector(
  selectStore,
  (s) => s.files.hasNextPage
);

export const selectTrashFolders = createSelector(
  selectStore,
  (s) => s.folders.folders
);

// Folder counterpart of selectSelectedTdoRows (same lifetime rule). It also
// drives the irreversible permanent folder delete.
export const selectSelectedFolderRows = createSelector(selectStore, (s) =>
  s.folders.selectedItems.map(
    (id) => s.folders.folders[id] ?? s.folders.selectedFolders[id]
  )
);

export const selectFolderSelectedItems = createSelector(
  selectStore,
  (s) => s.folders.selectedItems
);

export const selectIsFetchingFolderData = createSelector(
  selectStore,
  (s) => s.folders.isFetchingData
);

export const selectFolderPageSize = createSelector(
  selectStore,
  (s) => s.folders.pageSize
);
export const selectFolderPageIndex = createSelector(
  selectStore,
  (s) => s.folders.pageIndex
);
export const selectFolderHasNextPage = createSelector(
  selectStore,
  (s) => s.folders.hasNextPage
);

export const selectCaseFilesByCaseId = (id: TreeObjectId) =>
  createSelector(selectStore, (s) => s.folders.caseFiles[id]);
